import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, User, Shield, Settings } from 'lucide-react';
import { usersAPI } from '../../lib/api.js';
import { useRoles } from '../../hooks/useRoles.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { formatDate } from '../../utils/formatters.js';

function AdminUsersPage() {
  const [search, setSearch] = useState('');

  const { data: users, isLoading } = useQuery({
    queryKey: ['users', search],
    queryFn: () => usersAPI.getAll({ search }),
    // Removed refetchInterval to prevent unnecessary API calls
    // refetchInterval: 30000,
  });

  // Fetch roles from API
  const { data: rolesResponse } = useRoles();
  const roles = rolesResponse?.data?.data || [];

  const usersData = users?.data?.items || [];

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'superadmin':
        return 'danger';
      case 'admin':
        return 'primary';
      case 'viewer':
        return 'accent';
      default:
        return 'default';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return Shield;
      case 'manager':
        return Settings;
      default:
        return User;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            User Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage system users and permissions
          </p>
        </div>
        <Button variant="primary">
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Search */}
      <Card>
        <Input
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<Search className="h-4 w-4" />}
        />
      </Card>

      {/* Users list */}
      <Card>
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : usersData.length > 0 ? (
          <div className="space-y-4">
            {usersData.map((user) => {
              const RoleIcon = getRoleIcon(user.role);
              return (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center">
                      <RoleIcon className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                        {user.name}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {user.email}
                      </p>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Joined: {formatDate(user.createdAt)}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Company: {user.company?.name || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Badge variant={getRoleBadgeVariant(user.role)}>
                      {user.role}
                    </Badge>
                    <Badge variant={user.active ? 'success' : 'danger'}>
                      {user.active ? 'Active' : 'Inactive'}
                    </Badge>
                    <div className="flex items-center space-x-2">
                      <Button variant="outline" size="sm">
                        Edit
                      </Button>
                      <Button variant="danger" size="sm">
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              No users found. Add your first user to get started.
            </p>
          </div>
        )}
      </Card>

      {/* User statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {usersData.length}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Total Users
            </p>
          </div>
        </Card>
        {roles.slice(0, 2).map(role => (
          <Card key={role.id}>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {usersData.filter(u => u.role === role.name).length}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {role.name.charAt(0).toUpperCase() + role.name.slice(1).replace('_', ' ')}s
              </p>
            </div>
          </Card>
        ))}
        <Card>
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {usersData.filter(u => u.active).length}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Active Users
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default AdminUsersPage;
